import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sprout, X, Zap, Check, Award, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMBER = '#E7B64B';

const FALLBACK = { monthly: { launch: 12, standard: 15 }, annual: { launch: 99, standard: 129 } };
const TRIAL_DAYS = 14;

const PREMIUM_UNLOCKS = [
  'Unlimited AI growing advice',
  'Unlimited garden beds + biological intelligence',
  'Full Handbook — every regional sowing calendar',
  'GDD, Heat Units & Harvest Predictor',
  'Community posting & Fresh Harvests',
  'Vegetable Exchange listings & swap requests',
  'Garden Value tracker + every Tool',
];

function useCountdown(endMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, endMs - now);
  return {
    active: diff > 0,
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

function Paywall() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plan, setPlan] = useState('annual');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/subscription/me`, { withCredentials: true }).then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const launchEndsMs = me?.launch?.ends_at ? new Date(me.launch.ends_at).getTime() : Date.now() + 30 * 86400000;
  const cd = useCountdown(launchEndsMs);

  const pm = me?.pricing?.monthly;
  const py = me?.pricing?.annual;
  const monthly = pm ? (cd.active ? pm.launch : pm.standard) : (cd.active ? FALLBACK.monthly.launch : FALLBACK.monthly.standard);
  const annual = py ? (cd.active ? py.launch : py.standard) : (cd.active ? FALLBACK.annual.launch : FALLBACK.annual.standard);
  const stdMonthly = pm?.standard ?? FALLBACK.monthly.standard;
  const stdAnnual = py?.standard ?? FALLBACK.annual.standard;
  const monthlyWeekly = ((monthly * 12) / 52).toFixed(2);
  const annualWeekly = (annual / 52).toFixed(2);
  const roi = Math.max(2, Math.round(400 / annual));
  const alreadyPremium = !!me?.active;

  const dismiss = () => navigate(-1);

  const startTrial = async () => {
    if (alreadyPremium) { dismiss(); return; }
    setProcessing(true); setError(null);
    try {
      const tier_id = plan === 'annual' ? 'premium_yearly' : 'premium_monthly';
      const res = await axios.post(`${API}/subscription/checkout`, { tier_id, origin_url: window.location.origin }, { withCredentials: true });
      if (!res.data?.url) throw new Error('No checkout URL');
      window.location.href = res.data.url;
    } catch {
      setError("Couldn't start checkout. Please try again.");
      setProcessing(false);
    }
  };

  const pad = (n) => String(n).padStart(2, '0');
  const cdUnits = useMemo(() => ([{ v: cd.d, l: 'DAYS' }, { v: cd.h, l: 'HRS' }, { v: cd.m, l: 'MIN' }, { v: cd.s, l: 'SEC' }]), [cd.d, cd.h, cd.m, cd.s]);

  return (
    <div className="min-h-screen w-full relative overflow-hidden" data-testid="paywall-screen"
      style={{ background: 'radial-gradient(120% 80% at 10% 0%, #0C2318 0%, #0F2D1F 45%, #071711 100%)' }}>
      {/* Atmospheric blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-25" style={{ background: '#B4D330' }} />
      <div className="pointer-events-none absolute bottom-0 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-20" style={{ background: '#C9A85B' }} />

      <div className="relative max-w-2xl mx-auto px-5 pt-6 pb-56">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(180,211,48,0.14)' }}>
              <Sprout className="w-4 h-4" style={{ color: '#B4D330' }} />
            </div>
            <span className="text-[12px] font-bold tracking-[1.5px]" style={{ color: '#B4D330' }}>HOMEGROWN PREMIUM</span>
          </div>
          <button onClick={dismiss} data-testid="paywall-close" className="hg-press w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(250,246,236,0.12)' }}>
            <X className="w-5 h-5 text-[#FAF6EC]" />
          </button>
        </div>

        {/* Hero */}
        <div className="mt-5">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#FAF6EC] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Grow More with<br />Homegrown Premium.
          </h1>
          <p className="mt-3 text-[15px] max-w-md" style={{ color: 'rgba(250,246,236,0.78)' }}>
            Unlock everything and become the gardener you want to be.
          </p>
        </div>

        {/* Price flash + countdown */}
        {cd.active && (
          <div className="mt-5">
            <div className="flex items-center gap-2 flex-wrap" data-testid="paywall-price-flash">
              <span className="text-sm line-through" style={{ color: 'rgba(250,246,236,0.6)' }}>${stdMonthly}/mo</span>
              <span className="text-[15px] font-bold" style={{ color: '#B4D330' }}>${monthly}/mo</span>
              <span style={{ color: 'rgba(250,246,236,0.5)' }}>·</span>
              <span className="text-sm line-through" style={{ color: 'rgba(250,246,236,0.6)' }}>${stdAnnual}/yr</span>
              <span className="text-[15px] font-bold" style={{ color: '#B4D330' }}>${annual}/yr</span>
            </div>
            <div className="mt-3 rounded-2xl p-4 border" data-testid="paywall-countdown"
              style={{ borderColor: 'rgba(231,182,75,0.5)', background: 'rgba(12,35,24,0.5)', boxShadow: '0 0 22px -4px rgba(231,182,75,0.6)' }}>
              <div className="flex items-center justify-center gap-1.5 mb-2">
                <Zap className="w-3.5 h-3.5" style={{ color: AMBER }} />
                <span className="text-[11px] font-bold tracking-[1px]" style={{ color: AMBER }}>LAUNCH SPECIAL ENDS IN</span>
              </div>
              <div className="flex items-center justify-center gap-2.5">
                {cdUnits.map((u) => (
                  <div key={u.l} className="text-center">
                    <div className="min-w-[52px] py-2 px-2 rounded-lg" style={{ background: 'rgba(250,246,236,0.1)' }}>
                      <span className="text-xl font-bold text-[#FAF6EC]" style={{ fontFamily: 'Outfit, sans-serif' }}>{pad(u.v)}</span>
                    </div>
                    <span className="block mt-1 text-[9px] tracking-[1px]" style={{ color: 'rgba(250,246,236,0.55)' }}>{u.l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Plan cards */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PlanCard
            testId="paywall-plan-monthly"
            label="Most Flexible"
            selected={plan === 'monthly'}
            onSelect={() => setPlan('monthly')}
            price={`$${monthly}`}
            unit="/month"
            strike={cd.active ? `$${stdMonthly}` : undefined}
            weekly={`That's just $${monthlyWeekly} a week`}
            compare="Less than a bag of potting mix"
            chip="☕ Less than 2 coffees a week"
          />
          <PlanCard
            testId="paywall-plan-annual"
            label="Best Value"
            best
            selected={plan === 'annual'}
            onSelect={() => setPlan('annual')}
            price={`$${annual}`}
            unit="/year"
            strike={cd.active ? `$${stdAnnual}` : undefined}
            weekly={`Just $${annualWeekly} a week`}
            compare="Less than 3 pumpkins from the supermarket"
            chip="🌱 Your whole growing season, for the price of a Sunday market shop"
          />
        </div>

        {/* Value anchor */}
        <div className="mt-5 rounded-2xl p-4 border text-center" style={{ borderColor: 'rgba(107,159,74,0.5)', background: 'rgba(12,35,24,0.5)', boxShadow: '0 0 18px -4px rgba(107,159,74,0.6)' }}>
          <p className="text-sm leading-6" style={{ color: 'rgba(250,246,236,0.92)' }}>
            The average Homegrown Premium gardener grows over <span className="font-bold" style={{ color: '#B4D330' }}>$400</span> of vegetables a year. At ${annual}, that&apos;s a <span className="font-bold" style={{ color: '#B4D330' }}>{roi}x return</span> on your subscription.
          </p>
        </div>

        <p className="text-center text-sm mt-6" style={{ color: 'rgba(250,246,236,0.78)' }}>
          Start your {TRIAL_DAYS}-day free trial. Cancel anytime.
        </p>

        {/* Everything unlocked */}
        <div className="mt-6 rounded-2xl p-5 border" style={{ borderColor: 'rgba(250,246,236,0.12)', background: 'rgba(250,246,236,0.06)' }}>
          <p className="text-[11px] font-bold tracking-[1.5px] mb-3" style={{ color: '#B4D330' }}>EVERYTHING UNLOCKED</p>
          <div className="space-y-2.5">
            {PREMIUM_UNLOCKS.map((f) => (
              <div key={f} className="flex items-center gap-2.5">
                <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#B4D330' }}>
                  <Check className="w-3 h-3" style={{ color: '#0F2D1F' }} />
                </span>
                <span className="text-sm" style={{ color: 'rgba(250,246,236,0.9)' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 rounded-xl p-3 border" data-testid="paywall-error" style={{ borderColor: 'rgba(231,182,75,0.4)', background: 'rgba(231,182,75,0.14)' }}>
            <AlertCircle className="w-4 h-4" style={{ color: AMBER }} />
            <span className="text-sm text-[#FAF6EC]">{error}</span>
          </div>
        )}
      </div>

      {/* Sticky footer CTA */}
      <div className="fixed bottom-0 left-0 right-0">
        <div className="max-w-2xl mx-auto px-5 pt-4 pb-6" style={{ background: 'linear-gradient(to top, #071711 55%, rgba(7,23,17,0))' }}>
          <p className="text-center text-[12px] mb-2" style={{ color: 'rgba(250,246,236,0.7)' }}>
            A single bag of premium seeds costs more. This covers your entire garden, all year.
          </p>
          <button
            onClick={startTrial}
            disabled={processing}
            data-testid="paywall-start-trial"
            className="hg-press w-full h-14 rounded-full flex items-center justify-center gap-2 font-bold text-[#FAF6EC] border transition-all disabled:opacity-70"
            style={{ background: '#1A4331', borderColor: 'rgba(180,211,48,0.5)', boxShadow: '0 0 28px -6px rgba(180,211,48,0.8)' }}
          >
            {processing ? (
              <span className="w-5 h-5 border-2 border-[#FAF6EC] border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Sprout className="w-5 h-5" style={{ color: '#B4D330' }} />
                <span className="text-[17px]">{alreadyPremium ? "You're already Premium" : 'Start Free Trial'}</span>
              </>
            )}
          </button>
          <button onClick={dismiss} data-testid="paywall-maybe-later" className="w-full py-3 mt-1 font-semibold" style={{ color: 'rgba(250,246,236,0.72)' }}>
            Maybe Later
          </button>
          <p className="text-center text-[11px] mt-1" style={{ color: 'rgba(250,246,236,0.5)' }}>
            Billed automatically after trial. Cancel anytime in Settings.
          </p>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ label, price, unit, strike, weekly, compare, chip, best, selected, onSelect, testId }) {
  return (
    <button
      onClick={onSelect}
      data-testid={testId}
      aria-pressed={selected}
      className="hg-press relative text-left rounded-2xl p-5 border-2 transition-all"
      style={{
        background: 'rgba(250,246,236,0.9)',
        borderColor: selected ? '#B4D330' : 'rgba(250,246,236,0.3)',
        boxShadow: best ? '0 0 18px -4px rgba(107,159,74,0.85)' : 'none',
        minHeight: 244,
      }}
    >
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
        style={best ? { background: '#B4D330', color: '#0F2D1F' } : { background: '#8EAA97', color: '#1A4331' }}>
        {best && <Award className="w-3 h-3" />} {label}
      </span>
      <div className="flex items-end gap-1 mt-2">
        <span className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{price}</span>
        <span className="text-sm text-[#5C665C] mb-1">{unit}</span>
      </div>
      {strike ? <p className="text-[11px] line-through text-[#5C665C]">was {strike}</p> : <div className="h-[14px]" />}
      <p className="text-sm mt-1.5 font-semibold text-[#1A4331]">{weekly}</p>
      <p className="text-sm italic text-[#7A6B57] mt-0.5">{compare}</p>
      <div className="inline-block mt-3 px-2 py-1.5 rounded-lg" style={{ background: 'rgba(107,159,74,0.15)' }}>
        <span className="text-[10px] leading-[15px] font-semibold" style={{ color: '#3E6B2E' }}>{chip}</span>
      </div>
      <span className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${selected ? '' : ''}`}
        style={selected ? { background: '#1A4331', borderColor: '#1A4331' } : { background: 'rgba(255,255,255,0.4)', borderColor: '#B9C3B0' }}>
        {selected && <CheckCircle2 className="w-4 h-4 text-[#FAF6EC]" />}
      </span>
    </button>
  );
}

export default Paywall;
